import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

// ─── Types ──────────────────────────────────────────────────────────────────
// Not imported from @lensora/db — the dashboard does not depend on that
// package. This mirrors the wire shape returned by apps/api/src/routes/mcp/keys.ts.

export type McpApiKeyScope =
  | 'data:read'
  | 'scans:run'
  | 'reports:write'
  | 'content:write'
  | 'projects:write'
  // Server-derived only — granted exclusively by POST /api/mcp/keys/looker,
  // never selectable via the scope checklist.
  | 'reports:read'

/**
 * `mcp` keys are created via POST /api/mcp/keys with a user-picked scope
 * list. `looker_studio` keys are created via POST /api/mcp/keys/looker,
 * which hard-codes scope/purpose/restriction server-side — see
 * CreateLookerKeyInput below.
 */
export type McpApiKeyPurpose = 'mcp' | 'looker_studio'

export interface McpApiKey {
  id: string
  name: string
  keyPrefix: string
  purpose: McpApiKeyPurpose
  scopes: McpApiKeyScope[]
  restrictedClientId: string | null
  dailyCreditLimit: number | null
  expiresAt: string | null
  lastUsedAt: string | null
  lastClientName: string | null
  lastClientVersion: string | null
  revokedAt: string | null
  createdAt: string
}

export interface CreateMcpKeyInput {
  name: string
  scopes: McpApiKeyScope[]
  expiresInDays?: number | null
  restrictedClientId?: string | null
  dailyCreditLimit?: number | null
}

/**
 * Looker Studio keys take no scope/restriction/purpose input — the API
 * decides `scopes: ['reports:read']`, `purpose: 'looker_studio'`, and
 * `restrictedClientId` (derived from the caller's own billing scope)
 * server-side. Do not add fields here to "match" CreateMcpKeyInput.
 */
export interface CreateLookerKeyInput {
  name: string
  expiresInDays?: number | null
}

/**
 * The plaintext secret exists only in the return value of `mutateAsync` /
 * the `onSuccess` callback argument here — it is never written into the
 * `['mcp-keys']` query cache entry, only `key` is.
 */
export interface CreateMcpKeyResult {
  key: McpApiKey
  secret: string
}

export interface McpKeyUsageCall {
  id: string
  toolName: string
  status: 'ok' | 'error' | 'denied' | 'rate_limited'
  errorCode: string | null
  creditsCharged: number
  durationMs: number | null
  clientName: string | null
  createdAt: string
}

export interface McpKeyUsage {
  keyId: string
  totals: { calls: number; credits: number; windowDays: number }
  calls: McpKeyUsageCall[]
}

// ─── Data hooks ───────────────────────────────────────────────────────────────

export function useMcpKeys(enabled = true) {
  return useQuery({
    queryKey: ['mcp-keys'],
    queryFn: () => api.get<{ keys: McpApiKey[] }>('/api/mcp/keys').then((res) => res.keys),
    enabled,
  })
}

export function useCreateMcpKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateMcpKeyInput) =>
      api.post<CreateMcpKeyResult>('/api/mcp/keys', input),
    // Deliberately does not touch queryClient with `secret` — only the list
    // query is invalidated, which refetches the secret-free GET response.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-keys'] }),
  })
}

export function useCreateLookerKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateLookerKeyInput) =>
      api.post<CreateMcpKeyResult>('/api/mcp/keys/looker', input),
    // Same cache contract as useCreateMcpKey — secret only ever flows through
    // the mutation result, never into the ['mcp-keys'] cache entry.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-keys'] }),
  })
}

export function useRevokeMcpKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ revoked: true; key: McpApiKey }>(`/api/mcp/keys/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-keys'] }),
  })
}

export function useMcpKeyUsage(id: string | null, days: number) {
  return useQuery({
    queryKey: ['mcp-keys', id, 'usage', days],
    queryFn: () => api.get<McpKeyUsage>(`/api/mcp/keys/${id}/usage?days=${days}`),
    enabled: !!id,
  })
}
